import {
  Alert,
  AlertTitle,
  Box,
  LinearProgress,
  Link,
  Paper,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useCurrentUserContext } from 'contexts/CurrentUserContext'
import { useGetPdfReportQuery } from 'gqlTypes'
import React from 'react'
import { Tenant } from 'types'

type Props = {
  pdfReportId: string
  tenant: Tenant
}

const YtServicesLink: React.FC<{ isYt: boolean }> = ({ isYt }) => {
  if (isYt) {
    return (
      <Link href={'mailto:clientservices@youthtruthsurvey.org'}>
        clientservices@youthtruthsurvey.org.
      </Link>
    )
  }
  return <span>your client team.</span>
}

const PdfReportProgress: React.FC<Props> = ({ pdfReportId, tenant }) => {
  const currentUser = useCurrentUserContext()
  const isYt = tenant.title === 'YouthTruth'
  const isCep = tenant.title === 'CEP'
  const { data, error } = useGetPdfReportQuery({
    variables: {
      randomKey: pdfReportId,
    },
    pollInterval: 5000,
  })
  const value = data?.pdfReport?.percentComplete || 0
  if (value === 100) {
    window.location.reload()
  }

  return (
    <Paper square sx={{ p: 3, mx: 3, mt: 6 }}>
      <Box mb={2}>
        <Typography variant="h2">
          A PDF of your report is being compiled
        </Typography>
      </Box>
      {error ? (
        <>
          <Typography>
            We're sorry, an error has occured whilst preparing your report for
            printing.
          </Typography>
          <Typography>
            <span>Questions? Please contact </span>
            <YtServicesLink isYt={isYt} />
          </Typography>
        </>
      ) : (
        <Box>
          <Box mb={4}>
            <Box mt={4} mb={4}>
              <Alert
                severity="error"
                variant="outlined"
                sx={(theme) => ({
                  my: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  fontSize: 18,
                  fontWeight: theme.typography.fontWeightRegular,
                  '& .MuiAlert-icon': {
                    color: theme.palette.error.main,
                  },
                  '& .MuiAlertTitle-root': {
                    color: 'inherit',
                    fontSize: 'inherit',
                    fontWeight: theme.typography.fontWeightBold,
                  },
                })}
              >
                <AlertTitle>Do not edit this report</AlertTitle>
                Any edits made to the report while your PDF is being created
                will be reflected in the generated PDF
              </Alert>
            </Box>
            <Box mb={2}>
              <Typography>
                PDF generation can take anywhere from 1 – 60+ minutes to
                complete, depending on the size of the report.
              </Typography>
            </Box>
            <Box mb={2}>
              {currentUser && currentUser.email ? (
                <>
                  <Box mb={2}>
                    <Typography>
                      <span>
                        Once complete, you will receive an email from{' '}
                      </span>
                      <b>do-not-reply@surveyresults.org</b>
                      <span> with a link to your PDF.</span>
                    </Typography>
                    <Typography>
                      Please add this email address to your safe sender list or
                      check your junk mail folder.
                    </Typography>
                  </Box>
                  <Typography>
                    You may exit out of this window without interrupting the PDF
                    generation.
                  </Typography>
                </>
              ) : (
                <Typography>
                  <span>
                    You may exit out of this window without interrupting the PDF
                    generation, but{' '}
                    <b>
                      please save this survey URL and come back later to
                      retrieve the PDF.
                    </b>
                  </span>
                </Typography>
              )}
            </Box>
          </Box>
          <Box mt={6} display="flex" alignItems="center">
            <Box width="100%" mr={1}>
              <LinearProgress variant="determinate" value={value} />
            </Box>
            <Box minWidth={35}>
              <Typography variant="body2" color="textSecondary">{`${Math.round(
                value
              )}%`}</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  )
}

export default PdfReportProgress
